import { Component, Inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { disabled, form } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { LlmKirjausehdotusKategoria, LlmTestisettiKokoelma, LlmTestitapausKategoria } from '../../_jaettu-lemonator/model/llm-kirjausehdotus-testaus'
import { callLlmTestaus } from './llm-kirjausehdotus-testaus.client'

export interface LisaaLlmTestisettiinDialogData {
  asiakasAvain: string
  kirjausAvain: string
  kirjausnumero?: string | number
}

export interface LisaaLlmTestisettiinDialogResult {
  ok: boolean
  kategoria?: LlmKirjausehdotusKategoria
  settiAvaimet?: string[]
  /** Tosi jos vahvistus poisti testitapauksen kokonaan. */
  poistettu?: boolean
}

type DialogTila = 'lataa' | 'deterministinen' | 'valittavissa' | 'tallennetaan' | 'virhe'

@Component({
  templateUrl: './lisaa-testisettiin.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LisaaLlmTestisettiinDialog implements OnInit {
  readonly tila = signal<DialogTila>('lataa')
  readonly kategoria = signal<LlmKirjausehdotusKategoria | null>(null)
  readonly setit = signal<LlmTestisettiKokoelma[]>([])
  readonly valitutSettiAvaimet = signal<ReadonlySet<string>>(new Set())
  uudenSetinNimi = form(signal(''), polku => {
    disabled(polku, () => this.tila() === 'tallennetaan')
  })
  readonly latausvirhe = signal<string | null>(null)

  /** Lähtötilan jäsenyydet — käytetään diffin laskemiseen ja "ei muutoksia" -tilan tunnistamiseen. */
  private readonly _alkuperaisetSettiAvaimet = signal<ReadonlySet<string>>(new Set())

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LisaaLlmTestisettiinDialogData,
    private _dialogRef: MatDialogRef<LisaaLlmTestisettiinDialog, LisaaLlmTestisettiinDialogResult>,
    private _firebaseLemonator: FirebaseLemonator,
    private _snackbar: MatSnackBar,
  ) {}

  async ngOnInit() {
    await this._esikatsele()
  }

  private async _esikatsele() {
    this.tila.set('lataa')
    this.latausvirhe.set(null)
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'esikatseleKirjauksenKategoria', {
        asiakasAvain: this.data.asiakasAvain,
        kirjausAvain: this.data.kirjausAvain,
      })
      if (vastaus.e || !vastaus.kategoria) {
        this.tila.set('virhe')
        this.latausvirhe.set(vastaus.e === 'not-found' ? 'Kirjausta ei löytynyt.' : 'Kategorian tunnistaminen epäonnistui.')
        return
      }
      this.kategoria.set(vastaus.kategoria)
      if (vastaus.onDeterministinen) {
        this.tila.set('deterministinen')
        return
      }
      const olemassaOlevat = vastaus.settiAvaimet ?? []
      this._alkuperaisetSettiAvaimet.set(new Set(olemassaOlevat))
      this.valitutSettiAvaimet.set(new Set(olemassaOlevat))
      await this._lataaSetit()
      this.tila.set('valittavissa')
    } catch (err) {
      console.error('LisaaLlmTestisettiinDialog _esikatsele virhe', err)
      this.tila.set('virhe')
      this.latausvirhe.set('Kategorian tunnistaminen epäonnistui.')
    }
  }

  private async _lataaSetit() {
    if (!this.kategoria()) {
      return
    }
    // Deterministiset kategoriat on suodatettu pois ennen tähän asti — turvallista kaventaa.
    const kategoria = this.kategoria() as LlmTestitapausKategoria
    const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeSetit', { kategoria })
    this.setit.set(vastaus.setit ?? [])
  }

  toggleSetti(avain: string, checked: boolean) {
    if (checked) {
      this.valitutSettiAvaimet.update(joukko => new Set(joukko).add(avain))
    } else {
      this.valitutSettiAvaimet.update(joukko => new Set([...joukko].filter(arvo => arvo !== avain)))
    }
  }

  onSettiValittu(avain: string): boolean {
    return this.valitutSettiAvaimet().has(avain)
  }

  /** Onko kirjaus jo tallennettu johonkin settiin. */
  get onOlemassaolevaTestitapaus(): boolean {
    return this._alkuperaisetSettiAvaimet().size > 0
  }

  /** Vahvista sallittu kun valinnoissa on muutos lähtötilaan tai uuden setin nimi on annettu.
   *  Olemassaolevalle testitapaukselle myös kaikkien valintojen poisto sallittu (= testitapauksen poisto). */
  voiVahvistaa(): boolean {
    if (this.tila() !== 'valittavissa') {
      return false
    }
    const uusiNimi = (this.uudenSetinNimi().value() ?? '').trim()
    if (uusiNimi.length > 0) {
      return true
    }
    if (this._onMuutos()) {
      return true
    }
    return false
  }

  private _onMuutos(): boolean {
    if (this.valitutSettiAvaimet().size !== this._alkuperaisetSettiAvaimet().size) {
      return true
    }
    for (const a of this.valitutSettiAvaimet()) {
      if (!this._alkuperaisetSettiAvaimet().has(a)) {
        return true
      }
    }
    return false
  }

  async vahvista() {
    if (!this.voiVahvistaa() || !this.kategoria()) {
      return
    }
    this.tila.set('tallennetaan')

    const settiAvaimet: string[] = Array.from(this.valitutSettiAvaimet())

    // Deterministiset kategoriat on suodatettu pois ennen tähän asti — turvallista kaventaa.
    const kategoria = this.kategoria() as LlmTestitapausKategoria

    // Luo uusi setti tarvittaessa, ja lisää sen avain valittujen joukkoon.
    const uusiNimi = (this.uudenSetinNimi().value() ?? '').trim()
    if (uusiNimi.length > 0) {
      try {
        const luotu = await callLlmTestaus(this._firebaseLemonator, 'luoSetti', { kategoria, nimi: uusiNimi })
        if (luotu.e || !luotu.avain) {
          this._snackbar.open('Uuden setin luonti epäonnistui', 'Sulje', { duration: 4000 })
          this.tila.set('valittavissa')
          return
        }
        settiAvaimet.push(luotu.avain)
      } catch (err) {
        console.error('LisaaLlmTestisettiinDialog luoSetti virhe', err)
        this._snackbar.open('Uuden setin luonti epäonnistui', 'Sulje', { duration: 4000 })
        this.tila.set('valittavissa')
        return
      }
    }

    const poistetaanTestitapaus = settiAvaimet.length === 0 && this.onOlemassaolevaTestitapaus

    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'synkronoiKirjauksenJasenyydet', {
        asiakasAvain: this.data.asiakasAvain,
        kirjausAvain: this.data.kirjausAvain,
        settiAvaimet,
      })
      if (vastaus.e === 'kategoria-deterministinen') {
        // Race-suoja: tila muuttunut esikatselun jälkeen — siirrytään determinististä-näkymään.
        this.kategoria.set(vastaus.kategoria ?? this.kategoria())
        this.tila.set('deterministinen')
        return
      }
      if (vastaus.e === 'kategoria-mismatch') {
        this._snackbar.open('Kategoria muuttui — päivitetään lista', 'Sulje', { duration: 4000 })
        await this._lataaSetit()
        this.valitutSettiAvaimet.set(new Set(this._alkuperaisetSettiAvaimet()))
        this.tila.set('valittavissa')
        return
      }
      if (vastaus.e) {
        this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
        this.tila.set('valittavissa')
        return
      }
      if (poistetaanTestitapaus) {
        this._snackbar.open('Kirjaus poistettu LLM-testauksesta', 'Sulje', { duration: 3000 })
        this._dialogRef.close({ ok: true, kategoria: this.kategoria(), settiAvaimet: [], poistettu: true })
        return
      }
      this._snackbar.open(`Tallennettu (${settiAvaimet.length} setti${settiAvaimet.length === 1 ? '' : 'ä'})`, 'Sulje', { duration: 3000 })
      this._dialogRef.close({ ok: true, kategoria: this.kategoria(), settiAvaimet })
    } catch (err) {
      console.error('LisaaLlmTestisettiinDialog vahvista virhe', err)
      this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
      this.tila.set('valittavissa')
    }
  }

  peruuta() {
    this._dialogRef.close({ ok: false })
  }

  trackBySettiAvain(_index: number, setti: LlmTestisettiKokoelma): string {
    return setti.avain
  }
}
